import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Linkedin, Quote } from "lucide-react";
import { getSettings, getFounder, mediaUrl } from "@/lib/api";
import { Seo } from "@/components/site/Seo";
import { PageIntro } from "@/components/site/PageIntro";
import { SectionHeading } from "@/components/site/SectionHeading";
import { Reveal } from "@/components/site/Reveal";

export default function Leadership() {
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  const { data: founder, isLoading } = useQuery({ queryKey: ["founder"], queryFn: getFounder });

  if (isLoading || !founder) {
    return (
      <>
        <PageIntro eyebrow="Leadership" title="Leadership" />
        <div className="container-inst py-24"><div className="h-72 animate-pulse rounded-[14px] border" style={{ background: "var(--surface)", borderColor: "var(--card-border)" }} /></div>
      </>
    );
  }

  const portrait = mediaUrl(founder.portrait);

  return (
    <>
      <Seo title="Leadership" path="/leadership" settings={settings} description={`${founder.name}, ${founder.designation} of Acharya Holdings.`} />
      <PageIntro eyebrow="Leadership" title="Stewardship begins at the top."
        description="Acharya Holdings is guided by a long-term perspective and a conviction that enduring businesses are built through disciplined execution and responsible governance." />

      <div className="surface-cream">
      <section className="container-inst py-16 lg:py-24">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[320px_1fr] lg:gap-16">
          <Reveal>
            <div>
              <div className="overflow-hidden rounded-[16px] border" style={{ borderColor: "var(--card-border)", background: "var(--surface)" }}>
                {portrait ? (
                  <img src={portrait} alt={founder.name} className="aspect-[4/5] w-full object-cover" style={{ filter: "grayscale(0.15)" }} loading="lazy" />
                ) : (
                  <div className="flex aspect-[4/5] w-full items-center justify-center text-5xl font-semibold" style={{ color: "var(--accent)" }}>{founder.name?.charAt(0)}</div>
                )}
              </div>
              <div className="mt-5">
                <h2 className="text-xl" style={{ color: "var(--text)", fontWeight: 500 }}>{founder.name}</h2>
                <p className="mt-1 text-sm" style={{ color: "var(--muted)" }}>{founder.designation}</p>
                {founder.social?.linkedin && (
                  <a href={founder.social.linkedin} target="_blank" rel="noopener noreferrer" data-testid="founder-linkedin-link"
                    className="mt-4 inline-flex items-center gap-2 rounded-[10px] border px-3.5 py-2 text-sm transition-colors"
                    style={{ borderColor: "var(--card-border)", color: "var(--text)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--accent)")}
                    onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--card-border)")}>
                    <Linkedin size={15} /> LinkedIn
                  </a>
                )}
              </div>
            </div>
          </Reveal>

          <div>
            <Reveal>
              <p className="motif-caps">Biography</p>
              <p className="mt-4 max-w-[70ch] text-base leading-relaxed" style={{ color: "var(--muted)" }}>{founder.bio}</p>
            </Reveal>

            {founder.vision && (
              <Reveal delay={0.05}>
                <div className="mt-10">
                  <p className="motif-caps">Vision</p>
                  <p className="mt-4 max-w-[70ch] text-base leading-relaxed" style={{ color: "var(--muted)" }}>{founder.vision}</p>
                </div>
              </Reveal>
            )}

            {Array.isArray(founder.quotes) && founder.quotes.length > 0 && (
              <Reveal delay={0.1}>
                <blockquote className="mt-10 rounded-[14px] border p-6" style={{ background: "var(--surface)", borderColor: "var(--card-border)" }}>
                  <Quote size={18} style={{ color: "var(--accent)" }} />
                  <p className="mt-3 text-lg leading-relaxed" style={{ color: "var(--text)" }}>{founder.quotes[0].text}</p>
                </blockquote>
              </Reveal>
            )}
          </div>
        </div>

        {Array.isArray(founder.beliefs) && founder.beliefs.length > 0 && (
          <div className="mt-20">
            <SectionHeading eyebrow="Core Beliefs" title="The principles that guide us." />
            <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
              {founder.beliefs.map((b, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <div className="h-full rounded-[14px] border p-6" style={{ background: "var(--surface)", borderColor: "var(--card-border)" }}>
                    <h3 className="text-lg" style={{ color: "var(--text)", fontWeight: 500 }}>{b.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--muted)" }}>{b.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        )}

        {Array.isArray(founder.timeline) && founder.timeline.length > 0 && (
          <div className="mt-20">
            <SectionHeading eyebrow="Timeline" title="A path shaped by discipline." />
            <ol data-testid="leadership-timeline" className="mt-8">
              {founder.timeline.map((t, i) => (
                <li key={i} className="relative border-l pb-10 pl-8 last:pb-0" style={{ borderColor: "var(--card-border)" }}>
                  <span className="absolute -left-[7px] top-1 h-3.5 w-3.5 rounded-full border-2" style={{ background: "var(--bg)", borderColor: "var(--accent)" }} />
                  <p className="motif-caps">{t.year}</p>
                  <h3 className="mt-2 text-base" style={{ color: "var(--text)", fontWeight: 500 }}>{t.title}</h3>
                  <p className="mt-2 max-w-[70ch] text-sm leading-relaxed" style={{ color: "var(--muted)" }}>{t.text}</p>
                </li>
              ))}
            </ol>
          </div>
        )}
      </section>
      </div>
    </>
  );
}
